import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

/**
 * Reference unit option for a given type, from ToolsV2Metadata.
 */
interface ReferenceUnitOption {
    name: string;
    is_default: boolean;
    description?: string;
}

/**
 * References-table placement for a field. Only primary / secondary / tertiary
 * surface as columns in the compact table; sys_identifier, other, and null are
 * accepted but never shown there.
 */
export type DisplaySlot = 'sys_identifier' | 'primary' | 'secondary' | 'tertiary' | 'other';

/**
 * Per-field display metadata, mirrors query.cdm.type_def.FieldDisplayInfo.
 * Drives both the compact references table (via priority) and the sectioned
 * raw view (via section + display_label).
 */
export interface FieldDisplayInfo {
    display_label: string;
    priority?: DisplaySlot | null;
    /** Raw-view sub-section name; null/undefined = primary section. */
    section?: string | null;
    display?: boolean;
    /** False when the underlying column was suppressed by the availability map. */
    supported?: boolean;
}

/**
 * Type metadata entry from `GET /api/Matter/{pid}/ToolsV2Metadata`.
 */
interface AvailableType {
    name: string;
    reference_units: ReferenceUnitOption[];
    /**
     * CDM-only. Maps enriched-view column name → FieldDisplayInfo. Used by the
     * shared CDM references and raw views to render without per-type branching.
     * Null for non-CDM types (email_communication, edoc).
     */
    display_spec?: Record<string, FieldDisplayInfo> | null;
}

/**
 * A reference returned by the hydrate endpoint. Open shape — components read
 * various type-specific fields from `metadata`.
 */
export interface ReferenceUnitHandle {
    type: string;
    reference_unit: string;
    display_identifier?: string;
    content_hash?: string;
    chat_id?: string | null;
    search_units?: Record<string, boolean>;
    metadata?: Record<string, unknown>;
}

/**
 * Reference Unit Store
 *
 * Manages state for the Data Views V2 system:
 * - Available types and their reference units (from backend metadata)
 * - User's selected reference unit per type (session-scoped)
 */
export const useReferenceUnitStore = defineStore('referenceUnit', () => {
    console.log('[referenceUnitStore] Store initialized');

    const availableTypes = ref<AvailableType[]>([]);
    const selectedReferenceUnits = ref<Record<string, string>>({});

    // In-memory cache of loaded references, keyed by chat + the per-type
    // reference-unit selection. References for a completed chat are immutable,
    // so this lets the ReferencesPanel re-render instantly when it remounts
    // (e.g. after the references panel is collapsed and re-expanded) instead of
    // re-fetching and flashing a loading state. Lives on the store so it
    // survives the component unmount/remount.
    const referencesCache = new Map<string, unknown>();

    // Cache of fetched raw-view models for legacy types (EDoc / email
    // communication), keyed by matter + type + reference unit + content hash.
    // Re-selecting or navigating back to a reference is then instant instead of
    // re-POSTing /RawView. CDM raw views render from metadata and never fetch,
    // so they don't pass through here.
    const rawViewCache = new Map<string, unknown>();

    // True per-chat reference count, keyed by chat RecordId, derived from a
    // hydrated /Plan/References payload (sum of references[type].length — the
    // same number the references panel's per-type tabs total to). The chat
    // pill shows this once it's known; before a chat's references have been
    // hydrated there is no entry and the pill renders without a number. We
    // deliberately do NOT seed this from a backend approximation — see
    // docs_for_claude/reactive_orchestration/references-count-accuracy.md.
    const referenceCounts = ref<Record<string, number>>({});

    /** True reference count for a chat, or undefined if not hydrated yet. */
    function getReferenceCount(chatId: string): number | undefined {
        return referenceCounts.value[chatId];
    }

    /** Sum of per-type reference lists in a hydrated /Plan/References payload. */
    function _countReferences(data: unknown): number {
        const refs = (data as { references?: Record<string, unknown[]> } | null)?.references;
        if (!refs) return 0;
        return Object.values(refs).reduce(
            (sum, list) => sum + (Array.isArray(list) ? list.length : 0),
            0,
        );
    }

    const hasMetadata = computed(() => availableTypes.value.length > 0);

    /** Per-type reference-unit selection sent to /Plan/References. */
    function _currentReferenceUnitSpecs(): Record<string, string> {
        const specs: Record<string, string> = {};
        availableTypes.value.forEach((type) => {
            const selected = getSelectedReferenceUnit(type.name);
            if (selected) specs[type.name] = selected;
        });
        return specs;
    }

    function _referencesCacheKey(chatId: string): string {
        return `${chatId}::${JSON.stringify(_currentReferenceUnitSpecs())}`;
    }

    /**
     * Cached references for a chat under the current reference-unit selection,
     * or null when nothing is cached yet. Synchronous so callers can avoid a
     * loading flash on a cache hit.
     */
    function getCachedReferences(chatId: string): unknown | null {
        const cached = referencesCache.get(_referencesCacheKey(chatId)) ?? null;
        // Keep the derived pill count in step with whatever the panel renders,
        // including on the cache-hit path that bypasses loadReferences.
        if (cached !== null) {
            referenceCounts.value[chatId] = _countReferences(cached);
        }
        return cached;
    }

    async function loadMetadata(matterId: string) {
        console.log('[referenceUnitStore] Loading metadata for matter:', matterId);

        try {
            const response = await fetch(`/api/Matter/${matterId}/ToolsV2Metadata`);

            if (!response.ok) {
                throw new Error(`Failed to load metadata: ${response.status}`);
            }

            const data: { types: AvailableType[] } = await response.json();
            availableTypes.value = data.types;

            console.log('[referenceUnitStore] Loaded types:', data.types);

            data.types.forEach((type) => {
                const defaultUnit = type.reference_units.find((ru) => ru.is_default);
                if (defaultUnit && !selectedReferenceUnits.value[type.name]) {
                    selectedReferenceUnits.value[type.name] = defaultUnit.name;
                    console.log(
                        `[referenceUnitStore] Set default for ${type.name}: ${defaultUnit.name}`,
                    );
                }
            });
        } catch (err) {
            console.error('[referenceUnitStore] Error loading metadata:', err);
            throw err;
        }
    }

    function getSelectedReferenceUnit(typeName: string) {
        return selectedReferenceUnits.value[typeName];
    }

    function setSelectedReferenceUnit(typeName: string, referenceUnitName: string) {
        console.log(
            `[referenceUnitStore] Setting reference unit for ${typeName}: ${referenceUnitName}`,
        );
        selectedReferenceUnits.value[typeName] = referenceUnitName;
    }

    function getAvailableReferenceUnits(typeName: string) {
        const type = availableTypes.value.find((t) => t.name === typeName);
        return type ? type.reference_units : [];
    }

    /**
     * Returns the display_spec for a type (CDM types only).
     * Empty object when the type has no spec or isn't CDM.
     */
    function getDisplaySpec(typeName: string): Record<string, FieldDisplayInfo> {
        const type = availableTypes.value.find((t) => t.name === typeName);
        return type?.display_spec ?? {};
    }

    // Stable cache key for a reference's raw-view model. Null when there's no
    // content_hash to key on (then we just don't cache that one).
    function _rawViewCacheKey(reference: ReferenceUnitHandle, matterId: string): string | null {
        if (!reference.content_hash) return null;
        return `${matterId}::${reference.type}::${reference.reference_unit}::${reference.content_hash}`;
    }

    /**
     * Cached raw-view model for a reference, or null if not cached yet.
     * Synchronous so the raw-view components can skip the loading state on a hit.
     */
    function getCachedRawViewModel(reference: ReferenceUnitHandle, matterId: string): unknown | null {
        const key = _rawViewCacheKey(reference, matterId);
        if (!key) return null;
        return rawViewCache.has(key) ? rawViewCache.get(key) : null;
    }

    async function fetchRawViewModel(reference: ReferenceUnitHandle, matterId: string) {
        console.log('[referenceUnitStore] Fetching raw view model:', reference);

        const cacheKey = _rawViewCacheKey(reference, matterId);
        if (cacheKey && rawViewCache.has(cacheKey)) {
            console.log('[referenceUnitStore] Raw view model cache hit');
            return rawViewCache.get(cacheKey);
        }

        const typeName = reference.type;
        const referenceUnitName = reference.reference_unit;

        const url = `/api/Matter/${matterId}/RawView/${typeName}/${referenceUnitName}`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(reference),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.detail || `Failed to fetch raw view (${response.status})`,
                );
            }

            const rawViewModel = await response.json();
            console.log('[referenceUnitStore] Loaded raw view model:', rawViewModel);

            if (cacheKey) rawViewCache.set(cacheKey, rawViewModel);

            return rawViewModel;
        } catch (err) {
            console.error('[referenceUnitStore] Error fetching raw view:', err);
            throw err;
        }
    }

    async function loadReferences(chatId: string, matterId: string) {
        console.log('[referenceUnitStore] Loading references for chat:', chatId);

        const referenceUnitSpecs = _currentReferenceUnitSpecs();

        console.log('[referenceUnitStore] Reference unit specs:', referenceUnitSpecs);

        const url = `/api/Matter/${matterId}/Plan/References`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    chat_id: chatId,
                    reference_unit_specs: referenceUnitSpecs,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to load references');
            }

            const data = await response.json();
            console.log('[referenceUnitStore] Loaded references:', data);

            referencesCache.set(_referencesCacheKey(chatId), data);
            // Record the true per-chat count so the chat pill can show it. This
            // is the count the panel's per-type tabs total to.
            referenceCounts.value[chatId] = _countReferences(data);

            return data;
        } catch (err) {
            console.error('[referenceUnitStore] Error loading references:', err);
            throw err;
        }
    }

    return {
        availableTypes,
        selectedReferenceUnits,
        hasMetadata,
        loadMetadata,
        getSelectedReferenceUnit,
        setSelectedReferenceUnit,
        getAvailableReferenceUnits,
        getDisplaySpec,
        fetchRawViewModel,
        getCachedRawViewModel,
        loadReferences,
        getCachedReferences,
        getReferenceCount,
    };
});
